import React, { useEffect, Children } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useLanguage } from '../i18n/LanguageContext';
type Section = {
  number: string;
  title: string;
  intro?: string;
  body?: string[];
  groups?: {
    heading: string;
    items: string[];
  }[];
  outro?: string;
};
type PolicyContent = {
  badge: string;
  title: string;
  updated: string;
  intro: string[];
  agreement: string;
  sections: Section[];
  backToHome: string;
};
const POLICIES: Record<'ru' | 'kk' | 'en', PolicyContent> = {
  ru: {
    badge: 'Юридический документ',
    title: 'Политика конфиденциальности Meelo',
    updated: 'Дата последнего обновления: май 2026 года',
    intro: ['Добро пожаловать в Meelo — ERP-систему для частных дошкольных организаций и детских садов.', 'Настоящая Политика конфиденциальности объясняет, каким образом Meelo собирает, использует, хранит и защищает персональные данные при использовании сайта, мобильных приложений и сервисов платформы.'],
    agreement: 'Используя Meelo, вы соглашаетесь с условиями данной Политики конфиденциальности.',
    backToHome: '← На главную',
    sections: [{
      number: '1',
      title: 'Информация о компании',
      intro: 'Meelo — ERP-система для дошкольных образовательных организаций и детских садов.',
      body: ['Платформа может включать:'],
      groups: [{
        heading: '',
        items: ['цифровой документооборот', 'учет посещаемости', 'автоматизацию оплаты и заработной платы', 'расписания', 'управление питанием и закупками', 'коммуникацию с родителями', 'AI-помощника и аналитику']
      }, {
        heading: 'Контактная информация',
        items: ['ТОО «Meelo Digital Solutions»', 'БИН: 25054009597', 'Республика Казахстан, г. Алматы, м-н Ерменсай, ул. Талапты 34/2 корпус 2', 'Email: admin@pickme.kz', 'Телефон: +7 701 034 64 43']
      }]
    }, {
      number: '2',
      title: 'Какие данные мы собираем',
      intro: 'Мы можем собирать следующие категории информации:',
      groups: [{
        heading: 'Персональные данные',
        items: ['ФИО', 'номер телефона', 'адрес электронной почты', 'должность', 'название организации']
      }, {
        heading: 'Данные о детях',
        items: ['ФИО ребенка', 'данные посещаемости', 'информация о группе', 'контактные данные родителей или законных представителей']
      }, {
        heading: 'Операционные данные',
        items: ['графики и табели', 'финансовая информация', 'документы, загруженные в систему', 'журналы активности пользователей']
      }, {
        heading: 'Технические данные',
        items: ['IP-адрес', 'тип браузера', 'информация об устройстве', 'cookies и аналитические данные']
      }]
    }, {
      number: '3',
      title: 'Для чего используются данные',
      intro: 'Собранные данные используются для:',
      groups: [{
        heading: '',
        items: ['предоставления и поддержки платформы', 'автоматизации процессов детского сада', 'улучшения пользовательского опыта', 'предоставления поддержки', 'формирования отчетности и аналитики', 'обеспечения безопасности платформы', 'соблюдения требований законодательства']
      }]
    }, {
      number: '4',
      title: 'Правовые основания обработки',
      intro: 'Обработка персональных данных осуществляется:',
      groups: [{
        heading: '',
        items: ['с согласия пользователя', 'для исполнения договорных обязательств', 'в целях соблюдения законодательства', 'для законных интересов, связанных с работой и развитием платформы']
      }]
    }, {
      number: '5',
      title: 'Персональные данные детей',
      body: ['Meelo обрабатывает персональные данные детей исключительно в рамках деятельности образовательной организации и под ответственностью соответствующего детского сада или законного представителя.', 'Фото- и видеоматериалы детей могут обрабатываться только в рамках функционала образовательной организации, а доступ к ним ограничивается уполномоченными пользователями.']
    }, {
      number: '6',
      title: 'Передача данных третьим лицам',
      body: ['Мы не продаем персональные данные.', 'Информация может передаваться:'],
      groups: [{
        heading: '',
        items: ['уполномоченным сотрудникам', 'технологическим подрядчикам', 'государственным органам в случаях, предусмотренных законодательством', 'партнерам по интеграции сервисов']
      }],
      outro: 'Все третьи лица обязаны соблюдать требования конфиденциальности и защиты данных.'
    }, {
      number: '7',
      title: 'Хранение и защита данных',
      body: ['Персональные данные хранятся и обрабатываются на защищенных серверах, расположенных на территории Республики Казахстан.', 'Мы применяем организационные и технические меры для защиты информации от:'],
      groups: [{
        heading: '',
        items: ['несанкционированного доступа', 'изменения', 'раскрытия', 'уничтожения', 'потери данных']
      }],
      outro: 'Однако ни одна система не может гарантировать абсолютную безопасность.'
    }, {
      number: '8',
      title: 'Срок хранения данных',
      intro: 'Данные хранятся только в течение периода, необходимого:',
      groups: [{
        heading: '',
        items: ['для оказания услуг', 'выполнения юридических обязательств', 'разрешения споров', 'исполнения соглашений']
      }]
    }, {
      number: '9',
      title: 'Cookies',
      intro: 'Сайт может использовать cookies и аналитические технологии для:',
      groups: [{
        heading: '',
        items: ['улучшения работы платформы', 'анализа посещаемости', 'персонализации пользовательского опыта']
      }],
      outro: 'Пользователь может отключить cookies в настройках браузера.'
    }, {
      number: '10',
      title: 'Права пользователей',
      intro: 'Пользователь имеет право:',
      groups: [{
        heading: '',
        items: ['запросить доступ к своим данным', 'исправить неточные данные', 'запросить удаление данных', 'отозвать согласие', 'ограничить отдельные виды обработки']
      }],
      outro: 'Запросы могут быть направлены по указанным выше контактам.'
    }, {
      number: '11',
      title: 'AI disclaimer',
      body: ['Материалы, рекомендации и результаты, сгенерированные AI-функциями Meelo, предоставляются исключительно в качестве вспомогательного инструмента и не заменяют профессиональные педагогические, юридические, медицинские, бухгалтерские или регуляторные консультации.', 'Пользователь несет ответственность за проверку и применение информации на практике.']
    }, {
      number: '12',
      title: 'Изменения политики',
      body: ['Мы можем периодически обновлять данную Политику конфиденциальности. Актуальная версия всегда публикуется на сайте.']
    }, {
      number: '13',
      title: 'Применимое право',
      body: ['Настоящая Политика конфиденциальности регулируется законодательством Республики Казахстан.']
    }]
  },
  kk: {
    badge: 'Заңды құжат',
    title: 'Meelo құпиялылық саясаты',
    updated: 'Соңғы жаңарту күні: 2026 жыл, мамыр',
    intro: ['Meelo-ға қош келдіңіз — жеке балабақшалар мен мектепке дейінгі мекемелерге арналған ERP жүйесі.', 'Бұл құпиялылық саясаты сайтты, мобильді қосымшаларды және платформа қызметтерін пайдалану кезінде Meelo жеке деректерді қалай жинайтынын, пайдаланатынын, сақтайтынын және қорғайтынын түсіндіреді.'],
    agreement: 'Meelo-ны пайдалана отырып, сіз осы құпиялылық саясатының шарттарына келісесіз.',
    backToHome: '← Басты бетке',
    sections: [{
      number: '1',
      title: 'Компания туралы ақпарат',
      intro: 'Meelo — мектепке дейінгі білім беру ұйымдары мен балабақшаларға арналған ERP жүйесі.',
      body: ['Платформа мыналарды қамтуы мүмкін:'],
      groups: [{
        heading: '',
        items: ['цифрлық құжат айналымы', 'қатысуды есепке алу', 'төлем мен жалақыны автоматтандыру', 'кестелер', 'тамақтану мен сатып алуды басқару', 'ата-аналармен байланыс', 'AI көмекшісі және аналитика']
      }, {
        heading: 'Байланыс ақпараты',
        items: ['«Meelo Digital Solutions» ЖШС', 'БСН: 25054009597', 'Қазақстан Республикасы, Алматы қ., Ерменсай м/а, Талапты к. 34/2, 2-корпус', 'Email: admin@pickme.kz', 'Телефон: +7 701 034 64 43']
      }]
    }, {
      number: '2',
      title: 'Қандай деректер жинаймыз',
      intro: 'Біз келесі санаттағы ақпаратты жинай аламыз:',
      groups: [{
        heading: 'Жеке деректер',
        items: ['ТАӘ', 'телефон нөмірі', 'электрондық пошта мекенжайы', 'лауазымы', 'ұйым атауы']
      }, {
        heading: 'Балалар туралы деректер',
        items: ['баланың ТАӘ', 'қатысу деректері', 'топ туралы ақпарат', 'ата-аналардың немесе заңды өкілдердің байланыс деректері']
      }, {
        heading: 'Операциялық деректер',
        items: ['кестелер және табельдер', 'қаржылық ақпарат', 'жүйеге жүктелген құжаттар', 'пайдаланушы белсенділігі журналдары']
      }, {
        heading: 'Техникалық деректер',
        items: ['IP-мекенжай', 'браузер түрі', 'құрылғы туралы ақпарат', 'cookies және аналитикалық деректер']
      }]
    }, {
      number: '3',
      title: 'Деректер не үшін пайдаланылады',
      intro: 'Жиналған деректер мыналарға пайдаланылады:',
      groups: [{
        heading: '',
        items: ['платформаны ұсыну және қолдау', 'балабақша процестерін автоматтандыру', 'пайдаланушы тәжірибесін жақсарту', 'қолдау көрсету', 'есептілік пен аналитиканы қалыптастыру', 'платформа қауіпсіздігін қамтамасыз ету', 'заңнама талаптарын сақтау']
      }]
    }, {
      number: '4',
      title: 'Өңдеудің құқықтық негіздері',
      intro: 'Жеке деректерді өңдеу жүзеге асырылады:',
      groups: [{
        heading: '',
        items: ['пайдаланушы келісімімен', 'шарттық міндеттемелерді орындау үшін', 'заңнаманы сақтау мақсатында', 'платформаның жұмысы мен дамуына байланысты заңды мүдделер үшін']
      }]
    }, {
      number: '5',
      title: 'Балалардың жеке деректері',
      body: ['Meelo балалардың жеке деректерін тек білім беру ұйымының қызметі шеңберінде және тиісті балабақша немесе заңды өкіл жауапкершілігімен өңдейді.', 'Балалардың фото- және бейне материалдары тек білім беру ұйымының функционалы шеңберінде ғана өңделе алады, ал оларға қол жеткізу уәкілетті пайдаланушылармен шектеледі.']
    }, {
      number: '6',
      title: 'Деректерді үшінші тұлғаларға беру',
      body: ['Біз жеке деректерді сатпаймыз.', 'Ақпарат берілуі мүмкін:'],
      groups: [{
        heading: '',
        items: ['уәкілетті қызметкерлерге', 'технологиялық мердігерлерге', 'заңнамада көзделген жағдайларда мемлекеттік органдарға', 'сервистерді интеграциялау бойынша серіктестерге']
      }],
      outro: 'Барлық үшінші тұлғалар құпиялылық пен деректерді қорғау талаптарын сақтауға міндетті.'
    }, {
      number: '7',
      title: 'Деректерді сақтау және қорғау',
      body: ['Жеке деректер Қазақстан Республикасының аумағында орналасқан қорғалған серверлерде сақталады және өңделеді.', 'Біз ақпаратты қорғау үшін ұйымдастырушылық және техникалық шараларды қолданамыз:'],
      groups: [{
        heading: '',
        items: ['рұқсатсыз қол жеткізу', 'өзгерту', 'ашу', 'жою', 'деректерді жоғалту']
      }],
      outro: 'Алайда ешбір жүйе абсолютті қауіпсіздікке кепілдік бере алмайды.'
    }, {
      number: '8',
      title: 'Деректерді сақтау мерзімі',
      intro: 'Деректер тек қажетті кезеңге сақталады:',
      groups: [{
        heading: '',
        items: ['қызмет көрсету үшін', 'заңды міндеттемелерді орындау үшін', 'дауларды шешу үшін', 'келісімдерді орындау үшін']
      }]
    }, {
      number: '9',
      title: 'Cookies',
      intro: 'Сайт cookies және аналитикалық технологияларды пайдалана алады:',
      groups: [{
        heading: '',
        items: ['платформа жұмысын жақсарту', 'қатысуды талдау', 'пайдаланушы тәжірибесін жекелендіру']
      }],
      outro: 'Пайдаланушы cookies-ті браузер параметрлерінде өшіре алады.'
    }, {
      number: '10',
      title: 'Пайдаланушылардың құқықтары',
      intro: 'Пайдаланушының құқығы бар:',
      groups: [{
        heading: '',
        items: ['өз деректеріне қол жеткізуді сұрауға', 'нақты емес деректерді түзетуге', 'деректерді жоюды сұрауға', 'келісімін қайтарып алуға', 'жекелеген өңдеу түрлерін шектеуге']
      }],
      outro: 'Сұраулар жоғарыда көрсетілген байланыстар арқылы жіберілуі мүмкін.'
    }, {
      number: '11',
      title: 'AI disclaimer',
      body: ['Meelo AI-функцияларымен жасалған материалдар, ұсыныстар және нәтижелер тек қосалқы құрал ретінде ұсынылады және кәсіби педагогикалық, заңдық, медициналық, бухгалтерлік немесе реттеу кеңестерін ауыстырмайды.', 'Пайдаланушы ақпаратты тексеру және оны тәжірибеде қолдану үшін жауапты.']
    }, {
      number: '12',
      title: 'Саясат өзгерістері',
      body: ['Біз осы құпиялылық саясатын мерзімді түрде жаңартып отыра аламыз. Өзекті нұсқа әрқашан сайтта жарияланады.']
    }, {
      number: '13',
      title: 'Қолданылатын құқық',
      body: ['Осы құпиялылық саясаты Қазақстан Республикасының заңнамасымен реттеледі.']
    }]
  },
  en: {
    badge: 'Legal document',
    title: 'Privacy Policy of Meelo',
    updated: 'Last updated: May 2026',
    intro: ['Welcome to Meelo — an ERP platform for private early childhood education centers and kindergartens.', 'This Privacy Policy explains how Meelo collects, uses, stores, processes, and protects personal data when using our website, mobile applications, and services.'],
    agreement: 'By using Meelo, you agree to the terms of this Privacy Policy.',
    backToHome: '← Back to home',
    sections: [{
      number: '1',
      title: 'Company Information',
      intro: 'Meelo is an ERP system designed for preschool education organizations, including private kindergartens and childcare centers.',
      body: ['The platform may include:'],
      groups: [{
        heading: '',
        items: ['digital document management', 'attendance tracking', 'payroll and payment automation', 'scheduling', 'nutrition and procurement management', 'parent communication', 'AI-powered assistance and analytics']
      }, {
        heading: 'Contact Information',
        items: ['Meelo Digital Solutions LLP', 'BIN: 25054009597', 'Republic of Kazakhstan, Almaty, Yermensay microdistrict, Talapty street 34/2 building 2', 'Email: admin@pickme.kz', 'Phone: +7 701 034 64 43']
      }]
    }, {
      number: '2',
      title: 'What Information We Collect',
      intro: 'We may collect the following categories of information:',
      groups: [{
        heading: 'Personal Information',
        items: ['Full name', 'Phone number', 'Email address', 'Position / job title', 'Organization name']
      }, {
        heading: 'Child-Related Information',
        items: ["Child's full name", 'Attendance records', 'Group / class information', 'Parent / guardian contact details']
      }, {
        heading: 'Operational Information',
        items: ['Schedule and attendance data', 'Financial and payment information', 'Documents uploaded to the platform', 'System activity logs']
      }, {
        heading: 'Technical Information',
        items: ['IP address', 'Browser type', 'Device information', 'Cookies and analytics data']
      }]
    }, {
      number: '3',
      title: 'How We Use Information',
      intro: 'We use collected information to:',
      groups: [{
        heading: '',
        items: ['provide and maintain the platform', 'automate kindergarten operations', 'improve user experience', 'provide customer support', 'generate reports and analytics', 'ensure compliance with applicable regulations', 'maintain security and prevent unauthorized access']
      }]
    }, {
      number: '4',
      title: 'Legal Basis for Processing',
      intro: 'We process personal data:',
      groups: [{
        heading: '',
        items: ['with user consent', 'for performance of contractual obligations', 'to comply with legal requirements', 'for legitimate business interests related to operation and improvement of the platform']
      }]
    }, {
      number: '5',
      title: "Children's Personal Data",
      body: ["Meelo processes children's personal data only within the scope of educational organization activities and under the responsibility of the relevant kindergarten or legal representative.", 'Photo and video materials of children may be processed only within the functionality of the educational organization and access is restricted to authorized users.']
    }, {
      number: '6',
      title: 'Data Sharing',
      body: ['We do not sell personal data.', 'We may share information with:'],
      groups: [{
        heading: '',
        items: ['authorized employees and administrators', 'trusted technology service providers', 'government authorities when required by law', 'integration partners necessary for operation of the platform']
      }],
      outro: 'All third parties are required to maintain confidentiality and data protection standards.'
    }, {
      number: '7',
      title: 'Data Storage and Security',
      body: ['Personal data is stored and processed on secure servers located in the Republic of Kazakhstan.', 'We implement organizational and technical measures to protect information from:'],
      groups: [{
        heading: '',
        items: ['unauthorized access', 'alteration', 'disclosure', 'destruction', 'loss of data']
      }],
      outro: 'However, no system can guarantee absolute security.'
    }, {
      number: '8',
      title: 'Data Retention',
      intro: 'We retain personal data only for as long as necessary:',
      groups: [{
        heading: '',
        items: ['to provide services', 'comply with legal obligations', 'resolve disputes', 'enforce agreements']
      }]
    }, {
      number: '9',
      title: 'Cookies',
      intro: 'Our website may use cookies and analytics technologies to:',
      groups: [{
        heading: '',
        items: ['improve functionality', 'analyze traffic', 'personalize user experience']
      }],
      outro: 'Users may disable cookies through browser settings.'
    }, {
      number: '10',
      title: 'User Rights',
      intro: 'Depending on applicable law, users may have the right to:',
      groups: [{
        heading: '',
        items: ['request access to their data', 'request correction of inaccurate information', 'request deletion of data', 'withdraw consent', 'object to certain processing activities']
      }],
      outro: 'Requests may be sent to the contact information listed above.'
    }, {
      number: '11',
      title: 'AI Disclaimer',
      body: ['AI-generated materials, recommendations, and assistance provided by Meelo are intended solely as supportive tools and do not replace professional educational, legal, medical, accounting, or regulatory expertise.', 'Users remain responsible for verifying all information before applying it in practice.']
    }, {
      number: '12',
      title: 'Changes to This Policy',
      body: ['We may update this Privacy Policy periodically. Updated versions will be published on our website.']
    }, {
      number: '13',
      title: 'Governing Law',
      body: ['This Privacy Policy shall be governed by the laws of the Republic of Kazakhstan.']
    }]
  }
};
export function PrivacyPolicyPage() {
  const {
    lang
  } = useLanguage();
  const content = POLICIES[lang] || POLICIES.ru;
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'auto'
    });
  }, []);
  return <div className="min-h-screen bg-[#FAF7F2]" data-id="element-1199" data-mp="xrx37qkfqfdt">
      <Navbar data-id="element-1200" data-mp="xu9lk4u3urre" />

      {/* Decorative top gradient */}
      <div className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none" style={{
      background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(124,77,255,0.10) 0%, transparent 70%)'
    }} data-id="element-1201" data-mp="su51k4tznewf" />

      <main className="relative pt-28 pb-20 px-4 sm:px-6 lg:px-8" data-id="element-1202" data-mp="1ljlr8uq84hoy">
        <div className="max-w-3xl mx-auto" data-id="element-1203" data-mp="1gj7tc5ic6o4n">
          {/* Back link */}
          <a href="#" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#7C4DFF] hover:text-[#651FFF] transition-colors mb-8" data-id="element-1204" data-mp="1br67brvd9j1f">
            <ArrowLeftIcon size={15} strokeWidth={2.2} data-id="element-1205" data-mp="2480jtmg6w3rv" />
            {content.backToHome.replace('← ', '')}
          </a>

          {/* Header card */}
          <header className="mb-12" data-id="element-1206" data-mp="1zg8z3sjpss9n">
            <span className="inline-block text-[11px] font-bold tracking-[0.18em] uppercase text-[#7C4DFF] bg-[#EDE7F6] px-3 py-1.5 rounded-full mb-5" data-id="element-1207" data-mp="3tpnwgin29z54">
              {content.badge}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-bold text-gray-900 leading-[1.15] tracking-tight mb-4" data-id="element-1208" data-mp="3jzukhgj0bi0c">
              {content.title}
            </h1>
            <p className="text-sm text-gray-500 font-medium" data-id="element-1209" data-mp="3owkylyb943j4">
              {content.updated}
            </p>
          </header>

          {/* Document card */}
          <article className="bg-white rounded-3xl shadow-sm border border-gray-100 p-7 sm:p-10 lg:p-14" data-id="element-1210" data-mp="3fz0v0ipx1v5c">
            {/* Intro */}
            <div className="space-y-4 mb-10" data-id="element-1211" data-mp="312tf3uljjfvv">
              {content.intro.map((p, i) => <p key={i} className="text-[15px] sm:text-base text-gray-700 leading-relaxed" data-id="element-1212" data-mp="3601y1xeijny6" data-mpi={i}>
                  {p}
                </p>)}
              <div className="mt-6 p-4 rounded-2xl border-l-4 border-[#7C4DFF] bg-[#F5F3FF]" data-id="element-1213" data-mp="146cf369wrpuc">
                <p className="text-[15px] text-[#5B21B6] font-semibold leading-relaxed" data-id="element-1214" data-mp="2oqr3pl8yamyf">
                  {content.agreement}
                </p>
              </div>
            </div>

            {/* Sections */}
            <div className="space-y-12" data-id="element-1215" data-mp="2yi3tm7b6v31t">
              {content.sections.map(section => <section key={section.number} className="scroll-mt-28" data-id="element-1216" data-mp="2tnhim590rci9">
                  <div className="flex items-baseline gap-3 mb-4" data-id="element-1217" data-mp="37fe2jm0jag3k">
                    <span className="text-xs font-bold text-[#7C4DFF] tracking-widest" data-id="element-1218" data-mp="33g5biowvowbp">
                      {section.number.padStart(2, '0')}
                    </span>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight" data-id="element-1219" data-mp="3cg23p0ih8dbi">
                      {section.title}
                    </h2>
                  </div>

                  {/* Subtle divider */}
                  <div className="h-px bg-gray-100 mb-5" data-id="element-1220" data-mp="1i2khphxpzoil" />

                  {section.intro && <p className="text-[15px] text-gray-700 leading-relaxed mb-4" data-id="element-1221" data-mp="w9n4ciom47mf">
                      {section.intro}
                    </p>}

                  {section.body && section.body.map((p, i) => <p key={i} className="text-[15px] text-gray-700 leading-relaxed mb-3" data-id="element-1222" data-mp="160rhdcuq9iem" data-mpi={i}>
                        {p}
                      </p>)}

                  {section.groups && section.groups.map((group, gi) => <div key={gi} className={gi > 0 ? 'mt-5' : 'mt-2'} data-id="element-1223" data-mp="n7nmjo5rwzif" data-mpi={gi}>
                        {group.heading && <p className="text-sm font-semibold text-gray-900 mb-2.5" data-id="element-1224" data-mp="s8ba1zusmy7w">
                            {group.heading}
                          </p>}
                        <ul className="space-y-2" data-id="element-1225" data-mp="23lx7s9epy851">
                          {group.items.map((item, ii) => <li key={ii} className="flex items-start gap-3 text-[15px] text-gray-700 leading-relaxed" data-id="element-1226" data-mp="3o3fvqsaj42gz" data-mpi={ii}>
                              <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-[#7C4DFF]" aria-hidden="true" data-id="element-1227" data-mp="3edd5gzusl2mc" />
                              <span data-id="element-1228" data-mp="34htbwk3pag0r">{item}</span>
                            </li>)}
                        </ul>
                      </div>)}

                  {section.outro && <p className="text-[15px] text-gray-700 leading-relaxed mt-4" data-id="element-1229" data-mp="2vhmzz2nnho3p">
                      {section.outro}
                    </p>}
                </section>)}
            </div>
          </article>
        </div>
      </main>

      <Footer data-id="element-1230" data-mp="opvoz13n6gc9" />
    </div>;
}